import { PROMETHEUS_SERVICE } from '../bootstrap/cluster-diagnostics.js';
import { redactText } from '../bootstrap/redact.js';
/**
 * What the control plane asks an instance's own Prometheus (IM-207).
 *
 * Some of what went wrong on 24/7 NV is recorded only there: how many series
 * Prometheus holds and which metrics hold them, whether its scrape targets are
 * up, and what Kong answered. It is reached through the API server's service
 * proxy, on the token the cluster reader already holds, at the one service
 * the proxy Role names (`cluster-diagnostics.ts`). The path is a GET and the
 * Role grants nothing else, so a query here cannot change Prometheus.
 *
 * ## A fixed list, each with its reason
 *
 * Nothing here composes a query from input. `PROMETHEUS_QUERIES` is the whole
 * of what is asked, every query is an instant query, no range selector in one
 * reaches past `PROMETHEUS_LIMITS.maxWindow`, and each declares how many
 * answers it may keep. `prometheus.test.ts` holds all three. The investigator
 * (IM-198 phase 3) gets a bounded query tool on top of this, not instead of it.
 *
 * ## Kept as labels and a number
 *
 * An answer is reduced to the labels in `PROMETHEUS_LABELS` and its value. A
 * series can carry any label an exporter chose, and only these are read by a
 * check. Label values may name datasets and services, which was decided
 * acceptable (2026-10-27); they go through `redactText` all the same, as every
 * other value the cluster wrote does.
 *
 * ## Unknown is not done
 *
 * A Prometheus that does not answer — restarting, evicted, or not yet reachable
 * because no v20 run has opened port 9090 — makes the whole section
 * `unreadable` with the reason, and every check reading it `unknown`. A query
 * that fails on its own is unreadable on its own; the others still stand.
 */
/** Where the reader asks, below the cluster's endpoint. */
export const PROMETHEUS_PROXY_PATH = `/api/v1/namespaces/${PROMETHEUS_SERVICE.namespace}/services/${PROMETHEUS_SERVICE.name}:${String(PROMETHEUS_SERVICE.port)}/proxy`;
export const PROMETHEUS_LIMITS = {
    /** The widest range any query reads back. A day: what "today" and "since yesterday" need. */
    maxWindow: '1d',
    /** Asked of Prometheus, which abandons the query past it. Measured answers took 0.3 s. */
    queryTimeout: '3s',
    /**
     * Per request, from here. Short on purpose: before a v20 run opens 9090 the
     * proxy's dial hangs until this fires, in parallel with the cluster's other
     * reads, inside an API call with fifteen seconds in all.
     */
    requestTimeoutMs: 4_000,
    /** One answer, before projection. */
    maxResponseBytes: 1024 * 1024,
    /** How many metric names and label pairs the TSDB status is asked for. */
    tsdbTop: 10,
};
/** The only labels kept from an answer. */
export const PROMETHEUS_LABELS = [
    'job',
    'instance',
    'namespace',
    'service',
    'pod',
    'metrics_path',
];
/**
 * Everything asked, in one place. Each is cheap: one metric, one aggregation.
 * A count of series by metric name over every series hung on a real instance
 * (`es9kp25n`, 781,000 series); the TSDB status endpoint answers the same in a
 * third of a second, which is why the top metrics come from there instead.
 */
export const PROMETHEUS_QUERIES = [
    {
        id: 'headSeries',
        promql: 'max(prometheus_tsdb_head_series)',
        maxResults: 1,
        why: 'How many series Prometheus holds in memory now. Its memory grows with this, which is what evicted it on an 8 GiB node.',
    },
    {
        id: 'headSeriesPeak',
        promql: 'max(max_over_time(prometheus_tsdb_head_series[1d]))',
        maxResults: 1,
        why: 'The most it held in the last day, so a quiet moment does not hide the size it reaches.',
    },
    {
        id: 'targetsByJob',
        promql: 'count by (job) (up)',
        maxResults: 100,
        why: 'Which jobs are scraped at all, so a missing kubelet job is told apart from a kubelet that is down.',
    },
    {
        id: 'targetsDown',
        promql: 'up == 0',
        maxResults: 50,
        why: 'Scrape targets that did not answer. A down kubelet target means no container metrics for anything that reads them.',
    },
    {
        id: 'kongRequestsHour',
        promql: 'sum(increase(kong_http_requests_total[1h]))',
        maxResults: 1,
        why: 'Requests Kong answered in the last hour, against which its errors are a ratio.',
    },
    {
        id: 'kongErrorsHour',
        promql: 'sum(increase(kong_http_requests_total{code=~"5.."}[1h]))',
        maxResults: 1,
        why: 'Requests Kong answered with a server error in the last hour.',
    },
    {
        id: 'kongRequestsDay',
        promql: 'sum(increase(kong_http_requests_total[1d]))',
        maxResults: 1,
        why: 'The same over a day, so a bad hour is told apart from a bad day.',
    },
    {
        id: 'kongErrorsDay',
        promql: 'sum(increase(kong_http_requests_total{code=~"5.."}[1d]))',
        maxResults: 1,
        why: 'Server errors over a day.',
    },
    {
        id: 'kongErrorServices',
        promql: 'topk(5, sum by (service) (increase(kong_http_requests_total{code=~"5.."}[1d])) > 0)',
        maxResults: 5,
        why: 'The routes that answered most of those errors, which is where to look.',
    },
];
/** The query request for one spec. */
export const prometheusQueryPath = (query) => `${PROMETHEUS_PROXY_PATH}/api/v1/query?timeout=${PROMETHEUS_LIMITS.queryTimeout}&query=${encodeURIComponent(query.promql)}`;
/** The TSDB status request: the largest metrics and label pairs by series. */
export const PROMETHEUS_TSDB_PATH = `${PROMETHEUS_PROXY_PATH}/api/v1/status/tsdb?limit=${String(PROMETHEUS_LIMITS.tsdbTop)}`;
/**
 * Why Prometheus said nothing, in words a customer can act on. A number is
 * the API server's status: 403 is its Role, 404 the service, 503 no pod behind
 * it. A string is a transport failure's code, and `'timeout'` the request's
 * own limit firing, which is what a port nobody opened looks like.
 */
export const prometheusUnreadableReason = (status) => {
    if (status === 403) {
        return 'The cluster refused the Prometheus proxy. Its qrvey-diagnostics Role comes with dispatcher v20, on the next apply or output run with ClusterDiagnostics at read-only';
    }
    if (status === 404) {
        return `This cluster has no ${PROMETHEUS_SERVICE.name} service in ${PROMETHEUS_SERVICE.namespace}`;
    }
    if (status === 503) {
        return 'Prometheus is not answering: no ready pod is behind its service, or the cluster’s control plane cannot reach it on port 9090';
    }
    if (status === 'timeout') {
        return 'Prometheus did not answer in time. Until a dispatcher v20 apply or output run opens port 9090 to it from the control plane, the cluster’s API cannot reach it';
    }
    return typeof status === 'number'
        ? `Prometheus answered ${String(status)}`
        : `Prometheus could not be reached: ${status}`;
};
const isRecord = (value) => typeof value === 'object' && value !== null && !Array.isArray(value);
/** Prometheus writes a sample's value as a string; `NaN` and `+Inf` are not a reading. */
const sampleValue = (value) => {
    if (!Array.isArray(value) || typeof value[1] !== 'string')
        return undefined;
    const n = Number(value[1]);
    return Number.isFinite(n) ? n : undefined;
};
/**
 * One query's answer, as labels and a value, largest first and cut to the
 * spec's `maxResults`. Throws nothing: a body that is not a Prometheus answer
 * is unreadable, with that reason.
 */
export const projectQuery = (spec, body) => {
    let parsed;
    try {
        parsed = JSON.parse(body);
    }
    catch {
        return { state: 'unreadable', reason: 'Prometheus’s answer was not JSON.' };
    }
    const data = isRecord(parsed) ? parsed['data'] : undefined;
    if (!isRecord(parsed) || parsed['status'] !== 'success' || !isRecord(data)) {
        const error = isRecord(parsed) && typeof parsed['errorType'] === 'string'
            ? parsed['errorType']
            : 'no result';
        return { state: 'unreadable', reason: `Prometheus did not answer the query (${error}).` };
    }
    const result = Array.isArray(data['result']) ? data['result'] : [];
    const samples = result
        .flatMap((r) => {
        const value = isRecord(r) ? sampleValue(r['value']) : undefined;
        if (value === undefined)
            return [];
        const metric = isRecord(r) && isRecord(r['metric']) ? r['metric'] : {};
        const labels = {};
        for (const name of PROMETHEUS_LABELS) {
            const v = metric[name];
            if (typeof v === 'string')
                labels[name] = redactText(v);
        }
        return [{ labels, value }];
    })
        .sort((a, b) => b.value - a.value);
    const kept = samples.slice(0, spec.maxResults);
    return {
        state: 'read',
        samples: kept,
        ...(kept.length < samples.length
            ? {
                truncated: `Only the largest ${String(kept.length)} of ${String(samples.length)} answers are kept.`,
            }
            : {}),
    };
};
const counts = (value) => (Array.isArray(value) ? value : [])
    .flatMap((c) => isRecord(c) && typeof c['name'] === 'string' && typeof c['value'] === 'number'
    ? [{ name: redactText(c['name']), value: c['value'] }]
    : [])
    .sort((a, b) => b.value - a.value)
    .slice(0, PROMETHEUS_LIMITS.tsdbTop);
/** The TSDB status, as the head's series and the two top-ten lists. */
export const projectTsdb = (body) => {
    let parsed;
    try {
        parsed = JSON.parse(body);
    }
    catch {
        return { state: 'unreadable', reason: 'Prometheus’s answer was not JSON.' };
    }
    const data = isRecord(parsed) ? parsed['data'] : undefined;
    if (!isRecord(data))
        return { state: 'unreadable', reason: 'Prometheus gave no TSDB status.' };
    const head = data['headStats'];
    const series = isRecord(head) && typeof head['numSeries'] === 'number' ? head['numSeries'] : undefined;
    return {
        state: 'read',
        ...(series === undefined ? {} : { series }),
        topMetrics: counts(data['seriesCountByMetricName']),
        topLabelPairs: counts(data['seriesCountByLabelValuePair']),
    };
};
/** Every answer projected at once, as a reader that got every body would return them. */
export const prometheusStateFrom = (raw) => ({
    queries: Object.fromEntries(PROMETHEUS_QUERIES.map((q) => [q.id, projectQuery(q, raw.queries[q.id])])),
    tsdb: projectTsdb(raw.tsdb),
});
/** The one value of a single-answer query, when it was read and answered. */
export const singleValue = (read) => read.state === 'read' ? read.samples[0]?.value : undefined;
/**
 * The investigator's own query (IM-209), on top of the fixed list rather than
 * instead of it.
 *
 * The one place a query is composed from input — the model's — so it is held
 * to rules before it is sent: an instant query at the present moment, no range
 * selector or subquery reaching past `maxWindow`, a length cap, and
 * Prometheus's own timeout. An answer keeps more labels than a check reads,
 * because finding *which* container or node is the point of asking, and keeps
 * at most `maxResults` series, largest first. Every label value goes through
 * `redactText`.
 *
 * Reached through the same proxy and Role as the checks, so a query can read
 * and nothing else: the Role grants `get` on the one service, and the query
 * API writes nothing.
 */
export const INVESTIGATION_PROMQL = {
    /** A week: what an investigation of "since it started" needs. */
    maxWindow: '7d',
    maxWindowSeconds: 7 * 86_400,
    maxQueryChars: 500,
    maxResults: 50,
    labels: [
        ...PROMETHEUS_LABELS,
        'container',
        'node',
        'code',
        'phase',
        'reason',
        'queue',
        'le',
        'route',
        'persistentvolumeclaim',
    ],
};
const DURATION_UNITS = {
    ms: 0.001,
    s: 1,
    m: 60,
    h: 3_600,
    d: 86_400,
    w: 604_800,
    y: 31_536_000,
};
/** `1h30m` as seconds; undefined for anything that is not a Prometheus duration. */
export const promDurationSeconds = (text) => {
    const parts = [...text.matchAll(/(\d+)(ms|s|m|h|d|w|y)/g)];
    if (parts.length === 0 || parts.map((p) => p[0]).join('') !== text)
        return undefined;
    return parts.reduce((sum, p) => sum + Number(p[1]) * (DURATION_UNITS[p[2] ?? ''] ?? 0), 0);
};
/** Why a model's query cannot be asked, or nothing when it can. */
export const promqlProblem = (query) => {
    const trimmed = query.trim();
    if (trimmed === '')
        return 'A query is required.';
    if (trimmed.length > INVESTIGATION_PROMQL.maxQueryChars) {
        return `A query may be at most ${String(INVESTIGATION_PROMQL.maxQueryChars)} characters.`;
    }
    // Range selectors `[5m]` and subqueries `[1d:5m]`: every range and step has
    // to be a duration this reads, and no range may pass the window.
    for (const match of trimmed.matchAll(/\[([^\]]*)\]/g)) {
        const inside = match[1] ?? '';
        const [range, step] = inside.split(':');
        const seconds = promDurationSeconds((range ?? '').trim());
        if (seconds === undefined)
            return `A range selector must be a duration: [${inside}].`;
        if (step !== undefined &&
            step.trim() !== '' &&
            promDurationSeconds(step.trim()) === undefined) {
            return `A subquery step must be a duration: [${inside}].`;
        }
        if (seconds > INVESTIGATION_PROMQL.maxWindowSeconds) {
            return `No range may reach past ${INVESTIGATION_PROMQL.maxWindow}: [${inside}].`;
        }
    }
    // An offset moves the window back without widening a selector.
    for (const match of trimmed.matchAll(/\boffset\s+(-?[0-9a-z]+)/gi)) {
        const seconds = promDurationSeconds((match[1] ?? '').replace(/^-/, ''));
        if (seconds === undefined || seconds > INVESTIGATION_PROMQL.maxWindowSeconds) {
            return `No offset may reach past ${INVESTIGATION_PROMQL.maxWindow}.`;
        }
    }
    if (trimmed.includes('@'))
        return 'A query is asked at the present moment; @ is not accepted.';
    return undefined;
};
/** Where one investigator query is asked. Check `promqlProblem` first. */
export const investigationQueryPath = (query) => `${PROMETHEUS_PROXY_PATH}/api/v1/query?timeout=${PROMETHEUS_LIMITS.queryTimeout}&query=${encodeURIComponent(query.trim())}`;
/** An investigator query's answer, as kept labels and a value, largest first. */
export const projectInvestigationQuery = (body) => {
    let parsed;
    try {
        parsed = JSON.parse(body);
    }
    catch {
        return { state: 'unreadable', reason: 'Prometheus’s answer was not JSON.' };
    }
    const data = isRecord(parsed) ? parsed['data'] : undefined;
    if (!isRecord(parsed) || parsed['status'] !== 'success' || !isRecord(data)) {
        const error = isRecord(parsed) && typeof parsed['error'] === 'string'
            ? redactText(parsed['error'])
            : 'no result';
        return { state: 'unreadable', reason: `Prometheus did not answer the query (${error}).` };
    }
    const resultType = typeof data['resultType'] === 'string' ? data['resultType'] : 'unknown';
    if (resultType === 'scalar') {
        const value = sampleValue(data['result']);
        return {
            state: 'read',
            resultType,
            samples: value === undefined ? [] : [{ labels: {}, value }],
        };
    }
    if (resultType !== 'vector') {
        return {
            state: 'unreadable',
            reason: `Only instant vectors and scalars are read; this query returned a ${resultType}.`,
        };
    }
    const kept = new Set(INVESTIGATION_PROMQL.labels);
    const result = Array.isArray(data['result']) ? data['result'] : [];
    const samples = result
        .flatMap((r) => {
        const value = isRecord(r) ? sampleValue(r['value']) : undefined;
        if (value === undefined)
            return [];
        const metric = isRecord(r) && isRecord(r['metric']) ? r['metric'] : {};
        const labels = {};
        for (const [name, v] of Object.entries(metric)) {
            if (kept.has(name) && typeof v === 'string')
                labels[name] = redactText(v);
        }
        return [{ labels, value }];
    })
        .sort((a, b) => b.value - a.value);
    const cut = samples.slice(0, INVESTIGATION_PROMQL.maxResults);
    return {
        state: 'read',
        resultType,
        samples: cut,
        ...(cut.length < samples.length
            ? {
                truncated: `Only the largest ${String(cut.length)} of ${String(samples.length)} series are kept.`,
            }
            : {}),
    };
};
//# sourceMappingURL=prometheus.js.map